/**
 * Truy cập màn Quản lý nhiệm vụ của module `van-ban` (`/api/v1/m/van-ban/tasks*` và danh bạ
 * `/api/v1/m/van-ban/directory/*`, ADR-0084). Chọn API thật hay dữ liệu mẫu bằng `dungApiThat()` lúc gọi.
 *
 * Bản giả giữ cây trong bộ nhớ và kiểm lại vài luật của backend (việc cha không hoàn thành khi còn việc
 * con, hạn việc con không vượt việc cha, báo tiến độ trên việc chưa bắt đầu thì sang đang làm, việc cha
 * tự đồng bộ theo con), để view chạy đúng luồng khi không có backend.
 */
import { dungApiThat, goiApi, goiApiTep } from '../api/http';
import type { ApiError } from '../errors';
import {
  MOCK_CAU_NHIEM_VU as CAU,
  MOCK_DON_VI_DANH_BA,
  MOCK_NGUOI_DANH_BA,
  MOCK_TOI,
  mockGoiYPhanRa,
  mockHoatDong,
  mockNhiemVu,
} from '../../mock/van-ban-nhiem-vu';
import type { BanGhiNhiemVu } from '../../mock/van-ban-nhiem-vu';
import { mockRequest, mockState } from '../../mock/transport';
import { SAP_DEN_HAN_NGAY } from '../../modules/xu-ly-van-ban/nghiep-vu/nhiem-vu';
import type {
  BangTinThongBao,
  CapNhatVao,
  DonViDanhBa,
  GiaoNhiemVuVao,
  GoiYPhanRa,
  HoatDong,
  LocCay,
  LocKhoangNgay,
  NguoiDanhBa,
  NguoiThamGiaVao,
  NutNhiemVu,
  ThongKeNhiemVu,
  TrangCayNhiemVu,
  TrangDanhBa,
  TrangThaiNhiemVu,
  TruongNhiemVu,
  ViecConVao,
} from '../../modules/xu-ly-van-ban/types-nhiem-vu';

const GOC = '/api/v1/m/van-ban';
/** Danh bạ trả tối đa 100 dòng một trang (`DanhBaController`). */
export const DANH_BA_TOI_DA = 100;

// ─── Bản giả ─────────────────────────────────────────────────────────────────

let ds: BanGhiNhiemVu[] = mockNhiemVu();
const hoatDong: Record<string, HoatDong[]> = mockHoatDong();
let daXemLuc = new Date(0).toISOString();

const bayGio = () => new Date().toISOString();
const idMoi = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `mock-${Date.now()}`;
const homNay = () => new Date().toISOString().slice(0, 10);
const tuChoi = <T>(loi: ApiError): Promise<T> => mockRequest<null>(null).then(() => Promise.reject(loi));
const khongThay = (): ApiError => ({ code: 'NOT_FOUND', status: 404, message: CAU.khongThay });

const quaHan = (r: BanGhiNhiemVu) => r.deadline !== null && r.deadline < homNay() && r.status !== 'HOAN_THANH';
function sapDenHan(r: BanGhiNhiemVu) {
  if (!r.deadline || r.status === 'HOAN_THANH') return false;
  const toi = new Date();
  toi.setDate(toi.getDate() + SAP_DEN_HAN_NGAY);
  return r.deadline >= homNay() && r.deadline <= toi.toISOString().slice(0, 10);
}

function ghiHoatDong(taskId: string, h: Omit<HoatDong, 'id' | 'created_at'>) {
  hoatDong[taskId] = [{ ...h, id: idMoi(), created_at: bayGio() }, ...(hoatDong[taskId] ?? [])];
}

function thanhNut(r: BanGhiNhiemVu, coCon: boolean): NutNhiemVu {
  const con = ds.filter((c) => c.parent_id === r.id).sort((a, b) => a.created_at.localeCompare(b.created_at));
  const children = coCon ? con.map((c) => thanhNut(c, true)) : [];
  return {
    ...r,
    summarization_id: null,
    summarization_name: null,
    is_overdue: quaHan(r),
    child_total: con.length,
    child_done: con.filter((c) => c.status === 'HOAN_THANH').length,
    children,
  };
}

const goc = (r: BanGhiNhiemVu): BanGhiNhiemVu => {
  let cur = r;
  while (cur.parent_id) {
    const cha = ds.find((x) => x.id === cur.parent_id);
    if (!cha) break;
    cur = cha;
  }
  return cur;
};

function khopVaiTro(r: BanGhiNhiemVu, role: LocKhoangNgay['role']) {
  if (!role) return true;
  if (role === 'GIAO_VIEC') return r.created_by_id === MOCK_TOI.id;
  if (role === 'THAM_GIA') return r.participants.some((p) => p.user_id === MOCK_TOI.id);
  return r.participants.some((p) => p.user_id === MOCK_TOI.id && p.role === role);
}

function khopNgay(r: BanGhiNhiemVu, f: LocKhoangNgay) {
  const ngay = r.created_at.slice(0, 10);
  return (!f.date_from || ngay >= f.date_from) && (!f.date_to || ngay <= f.date_to);
}

function khopTrangThai(r: BanGhiNhiemVu, s: LocCay['task_status']) {
  if (!s) return true;
  if (s === 'QUA_HAN') return quaHan(r);
  if (s === 'SAP_DEN_HAN') return sapDenHan(r);
  if (s === 'HOAN_THANH') return r.status === 'HOAN_THANH';
  return r.status === s && !quaHan(r);
}

/** Xong xếp cuối; còn lại hạn gần lên trước, không hạn sau cùng; hoà thì mới trước. */
function mucGap(a: BanGhiNhiemVu, b: BanGhiNhiemVu) {
  const xa = Number(a.status === 'HOAN_THANH') - Number(b.status === 'HOAN_THANH');
  if (xa) return xa;
  const ha = Number(a.deadline === null) - Number(b.deadline === null);
  if (ha) return ha;
  if (a.deadline && b.deadline && a.deadline !== b.deadline) return a.deadline.localeCompare(b.deadline);
  return b.created_at.localeCompare(a.created_at);
}

/** Đồng bộ trạng thái việc cha theo con, lan lên gốc — như `dongBoCha` của backend. */
function dongBoCha(chaId: string | null) {
  let id = chaId;
  while (id) {
    const cha = ds.find((x) => x.id === id);
    if (!cha) return;
    const con = ds.filter((c) => c.parent_id === id);
    if (con.length === 0) return;
    let moi: TrangThaiNhiemVu = cha.status;
    if (con.every((c) => c.status === 'HOAN_THANH')) moi = 'HOAN_THANH';
    else if (cha.status === 'HOAN_THANH' || con.some((c) => c.status !== 'MOI_PHAN_CONG')) moi = 'DANG_THUC_HIEN';
    if (moi === cha.status) return;
    datTrangThai(cha, moi, null);
    id = cha.parent_id;
  }
}

function datTrangThai(r: BanGhiNhiemVu, moi: TrangThaiNhiemVu, actor: { id: string; name: string } | null) {
  if (r.status === moi) return;
  ghiHoatDong(r.id, {
    kind: 'STATUS_CHANGED',
    actor_id: actor?.id ?? null,
    actor_name: actor?.name ?? null,
    content: null,
    from_status: r.status,
    to_status: moi,
    progress: null,
    attachment_filename: null,
  });
  r.status = moi;
  r.completed_at = moi === 'HOAN_THANH' ? bayGio() : null;
  if (moi === 'HOAN_THANH') r.progress = 100;
  r.updated_at = bayGio();
}

const NGUOI_TOI = { id: MOCK_TOI.id, name: MOCK_TOI.name };
const voiId = (p: NguoiThamGiaVao) => ({ ...p, id: idMoi() });

function taoBanGhi(vao: {
  parent_id: string | null;
  title: string;
  description: string | null;
  deadline: string | null;
  lead_org_id: string | null;
  lead_org_name: string | null;
  related_document: string | null;
  attachment_filename: string | null;
  participants: NguoiThamGiaVao[];
}): BanGhiNhiemVu {
  const r: BanGhiNhiemVu = {
    ...vao,
    id: idMoi(),
    status: 'MOI_PHAN_CONG',
    created_by_id: MOCK_TOI.id,
    created_by_name: MOCK_TOI.name,
    completed_at: null,
    progress: 0,
    created_at: bayGio(),
    updated_at: bayGio(),
    participants: vao.participants.map(voiId),
  };
  ds = [...ds, r];
  const chung = { actor_id: MOCK_TOI.id, actor_name: MOCK_TOI.name, from_status: null, to_status: null, progress: null, attachment_filename: null };
  ghiHoatDong(r.id, { ...chung, kind: 'CREATED', content: null });
  if (vao.parent_id) {
    ghiHoatDong(vao.parent_id, { ...chung, kind: 'CHILD_ADDED', content: JSON.stringify({ id: r.id, title: r.title }) });
  }
  return r;
}

function trangDanhBa<T>(items: T[]): TrangDanhBa<T> {
  return { items, page: { page: 1, page_size: DANH_BA_TOI_DA, total_items: items.length, total_pages: 1 } };
}

const banGia = {
  thongKe: (f: LocKhoangNgay): Promise<ThongKeNhiemVu> => {
    const loc = ds.filter((r) => khopNgay(r, f) && khopVaiTro(r, f.role));
    const dem = (p: (r: BanGhiNhiemVu) => boolean, tap = loc) => tap.filter(p).length;
    const theoDonVi = new Map<string, BanGhiNhiemVu[]>();
    for (const r of loc) {
      const ten = r.lead_org_name ?? CAU.chuaRoDonVi;
      theoDonVi.set(ten, [...(theoDonVi.get(ten) ?? []), r]);
    }
    const kq: ThongKeNhiemVu = {
      total: loc.length,
      moi_phan_cong: dem((r) => r.status === 'MOI_PHAN_CONG' && !quaHan(r)),
      dang_thuc_hien: dem((r) => r.status === 'DANG_THUC_HIEN' && !quaHan(r)),
      qua_han: dem(quaHan),
      hoan_thanh: dem((r) => r.status === 'HOAN_THANH'),
      sap_den_han: dem(sapDenHan),
      by_org: [...theoDonVi.entries()]
        .map(([org_name, tap]) => ({
          org_name,
          total: tap.length,
          moi_phan_cong: dem((r) => r.status === 'MOI_PHAN_CONG' && !quaHan(r), tap),
          dang_thuc_hien: dem((r) => r.status === 'DANG_THUC_HIEN' && !quaHan(r), tap),
          hoan_thanh: dem((r) => r.status === 'HOAN_THANH', tap),
          qua_han: dem(quaHan, tap),
        }))
        .sort((a, b) => b.total - a.total || a.org_name.localeCompare(b.org_name)),
    };
    const rong: ThongKeNhiemVu = { total: 0, moi_phan_cong: 0, dang_thuc_hien: 0, qua_han: 0, hoan_thanh: 0, sap_den_han: 0, by_org: [] };
    return mockRequest(kq, rong);
  },
  cay: (f: LocCay): Promise<TrangCayNhiemVu> => {
    const q = f.search?.toLocaleLowerCase('vi');
    const khop = ds.filter(
      (r) =>
        khopNgay(r, f) &&
        khopVaiTro(r, f.role) &&
        khopTrangThai(r, f.task_status) &&
        (!f.lead_org_id || r.lead_org_id === f.lead_org_id) &&
        (!q ||
          [r.title, r.description ?? '', r.related_document ?? '', ...r.participants.map((p) => p.user_name)].some((s) =>
            s.toLocaleLowerCase('vi').includes(q),
          )),
    );
    const goc_ = [...new Map(khop.map((r) => [goc(r).id, goc(r)])).values()].sort(mucGap);
    const tap = mockState() === 'filtered-empty' ? [] : goc_;
    const trang = tap.slice((f.page - 1) * f.page_size, f.page * f.page_size).map((r) => thanhNut(r, true));
    const rong: TrangCayNhiemVu = { data: [], total: 0, page: 1, page_size: f.page_size, total_pages: 1 };
    return mockRequest(
      {
        data: trang,
        total: tap.length,
        page: f.page,
        page_size: f.page_size,
        total_pages: Math.max(1, Math.ceil(tap.length / f.page_size)),
      },
      rong,
    );
  },
  mot: (id: string): Promise<NutNhiemVu> => {
    const r = ds.find((x) => x.id === id);
    return r ? mockRequest(thanhNut(r, false)) : tuChoi(khongThay());
  },
  tao: (vao: GiaoNhiemVuVao): Promise<NutNhiemVu> => {
    if (vao.parentId && !ds.some((x) => x.id === vao.parentId)) return tuChoi(khongThay());
    const r = taoBanGhi({
      parent_id: vao.parentId ?? null,
      title: vao.title,
      description: vao.description || null,
      deadline: vao.deadline || null,
      lead_org_id: vao.leadOrgId || null,
      lead_org_name: vao.leadOrgId ? vao.leadOrgName : null,
      related_document: null,
      attachment_filename: vao.file?.name ?? null,
      participants: vao.participants,
    });
    dongBoCha(r.parent_id);
    return mockRequest(thanhNut(r, false));
  },
  sua: (id: string, vao: TruongNhiemVu): Promise<NutNhiemVu> => {
    const r = ds.find((x) => x.id === id);
    if (!r) return tuChoi(khongThay());
    const doi: string[] = [];
    if (vao.title !== r.title) doi.push('title');
    if ((vao.description || null) !== r.description) doi.push('description');
    if ((vao.deadline || null) !== r.deadline) doi.push('deadline');
    if ((vao.leadOrgId || null) !== r.lead_org_id) doi.push('lead_org');
    if (vao.file) doi.push('attachment');
    Object.assign(r, {
      title: vao.title,
      description: vao.description || null,
      deadline: vao.deadline || null,
      lead_org_id: vao.leadOrgId || null,
      lead_org_name: vao.leadOrgId ? vao.leadOrgName : null,
      attachment_filename: vao.file?.name ?? r.attachment_filename,
      updated_at: bayGio(),
    });
    if (doi.length > 0) {
      ghiHoatDong(id, {
        kind: 'UPDATED',
        actor_id: MOCK_TOI.id,
        actor_name: MOCK_TOI.name,
        content: JSON.stringify({ fields: doi.sort() }),
        from_status: null,
        to_status: null,
        progress: null,
        attachment_filename: null,
      });
    }
    return mockRequest(thanhNut(r, false));
  },
  nguoiThamGia: (id: string, ps: NguoiThamGiaVao[]): Promise<NutNhiemVu> => {
    const r = ds.find((x) => x.id === id);
    if (!r) return tuChoi(khongThay());
    const truoc = new Map(r.participants.map((p) => [p.user_id, p]));
    const sau = new Map(ps.map((p) => [p.user_id, p]));
    const diff = {
      added: ps.filter((p) => !truoc.has(p.user_id)).map((p) => ({ name: p.user_name, role: p.role })),
      removed: r.participants.filter((p) => !sau.has(p.user_id)).map((p) => ({ name: p.user_name, role: p.role })),
      role_changed: ps
        .filter((p) => truoc.has(p.user_id) && truoc.get(p.user_id)?.role !== p.role)
        .map((p) => ({ name: p.user_name, role: p.role })),
    };
    r.participants = ps.map(voiId);
    if (diff.added.length + diff.removed.length + diff.role_changed.length > 0) {
      ghiHoatDong(id, {
        kind: 'PARTICIPANTS_CHANGED',
        actor_id: MOCK_TOI.id,
        actor_name: MOCK_TOI.name,
        content: JSON.stringify(diff),
        from_status: null,
        to_status: null,
        progress: null,
        attachment_filename: null,
      });
    }
    return mockRequest(thanhNut(r, false));
  },
  trangThai: (id: string, status: TrangThaiNhiemVu): Promise<NutNhiemVu> => {
    const r = ds.find((x) => x.id === id);
    if (!r) return tuChoi(khongThay());
    const conDo = ds.filter((c) => c.parent_id === id && c.status !== 'HOAN_THANH').length;
    if (status === 'HOAN_THANH' && conDo > 0) {
      return tuChoi({ code: 'VAN_BAN_STATUS_CONFLICT', status: 409, message: CAU.conViecCon.replace('{{so}}', String(conDo)) });
    }
    datTrangThai(r, status, NGUOI_TOI);
    dongBoCha(r.parent_id);
    return mockRequest(thanhNut(r, false));
  },
  xoa: (id: string): Promise<void> => {
    const r = ds.find((x) => x.id === id);
    if (!r) return tuChoi(khongThay());
    const xoa = new Set([id]);
    let them = true;
    while (them) {
      them = false;
      for (const x of ds) {
        if (x.parent_id && xoa.has(x.parent_id) && !xoa.has(x.id)) {
          xoa.add(x.id);
          them = true;
        }
      }
    }
    ds = ds.filter((x) => !xoa.has(x.id));
    dongBoCha(r.parent_id);
    return mockRequest(undefined);
  },
  tep: (id: string): Promise<Blob> => {
    const r = ds.find((x) => x.id === id);
    if (!r?.attachment_filename) return tuChoi({ code: 'NOT_FOUND', status: 404, message: CAU.khongCoTep });
    return mockRequest(new Blob([CAU.tepMau], { type: 'text/plain' }));
  },
  dongThoiGian: (id: string): Promise<HoatDong[]> => {
    if (!ds.some((x) => x.id === id)) return tuChoi(khongThay());
    return mockRequest(hoatDong[id] ?? [], []);
  },
  themCapNhat: (id: string, vao: CapNhatVao): Promise<HoatDong> => {
    const r = ds.find((x) => x.id === id);
    if (!r) return tuChoi(khongThay());
    ghiHoatDong(id, {
      kind: vao.kind,
      actor_id: MOCK_TOI.id,
      actor_name: MOCK_TOI.name,
      content: vao.content.trim() || null,
      from_status: null,
      to_status: null,
      progress: vao.kind === 'PROGRESS' ? (vao.progress ?? 0) : null,
      attachment_filename: vao.file?.name ?? null,
    });
    const moi = hoatDong[id]![0]!;
    if (vao.kind === 'PROGRESS') {
      r.progress = vao.progress ?? 0;
      if (r.progress > 0 && r.status === 'MOI_PHAN_CONG') {
        datTrangThai(r, 'DANG_THUC_HIEN', NGUOI_TOI);
        dongBoCha(r.parent_id);
      }
    }
    return mockRequest(moi);
  },
  tepCapNhat: (taskId: string, activityId: string): Promise<Blob> => {
    const h = (hoatDong[taskId] ?? []).find((x) => x.id === activityId);
    if (!h?.attachment_filename) return tuChoi({ code: 'NOT_FOUND', status: 404, message: CAU.khongCoTep });
    return mockRequest(new Blob([CAU.tepMau], { type: 'text/plain' }));
  },
  goiYPhanRa: (id: string): Promise<GoiYPhanRa> => {
    const r = ds.find((x) => x.id === id);
    if (!r) return tuChoi(khongThay());
    const han = r.deadline ?? homNay();
    return mockRequest({
      parent_id: id,
      used_attachment: Boolean(r.attachment_filename),
      items: mockGoiYPhanRa(han),
    });
  },
  taoViecCon: (id: string, con: ViecConVao[]): Promise<NutNhiemVu> => {
    const cha = ds.find((x) => x.id === id);
    if (!cha) return tuChoi(khongThay());
    const vuot = con.find((c) => cha.deadline && c.deadline && c.deadline > cha.deadline);
    if (vuot) {
      return tuChoi({ code: 'INVALID_ARGUMENT', status: 400, message: CAU.hanVuot.replace('{{ten}}', vuot.title.slice(0, 60)) });
    }
    for (const c of con) {
      taoBanGhi({
        parent_id: id,
        title: c.title,
        description: c.description ?? null,
        deadline: c.deadline ?? null,
        lead_org_id: c.lead_org_id ?? null,
        lead_org_name: c.lead_org_id ? (c.lead_org_name ?? null) : null,
        related_document: cha.related_document,
        attachment_filename: null,
        participants: c.participants,
      });
    }
    dongBoCha(id);
    return mockRequest(thanhNut(cha, false));
  },
  thongBao: (limit: number): Promise<BangTinThongBao> => {
    const cuaToi = ds.filter(
      (r) => r.created_by_id === MOCK_TOI.id || r.participants.some((p) => p.user_id === MOCK_TOI.id),
    );
    const items = cuaToi
      .flatMap((r) =>
        (hoatDong[r.id] ?? [])
          .filter((h) => h.actor_id !== MOCK_TOI.id)
          .map((h) => ({ ...h, task_id: r.id, task_title: r.title, unread: h.created_at > daXemLuc })),
      )
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
    return mockRequest(
      { unread_count: items.filter((i) => i.unread).length, items: items.slice(0, limit) },
      { unread_count: 0, items: [] },
    );
  },
  daXem: (): Promise<void> => {
    daXemLuc = bayGio();
    return mockRequest(undefined);
  },
  donViDanhBa: (): Promise<TrangDanhBa<DonViDanhBa>> =>
    mockRequest(trangDanhBa(MOCK_DON_VI_DANH_BA), trangDanhBa<DonViDanhBa>([])),
  nguoiDanhBa: (search: string | undefined, pageSize: number): Promise<TrangDanhBa<NguoiDanhBa>> => {
    const q = search?.toLocaleLowerCase('vi') ?? '';
    return mockRequest(
      trangDanhBa(MOCK_NGUOI_DANH_BA.filter((u) => u.full_name.toLocaleLowerCase('vi').includes(q)).slice(0, pageSize)),
    );
  },
};

// ─── Bản thật ────────────────────────────────────────────────────────────────

const ma = (id: string) => encodeURIComponent(id);
function bieuMauTruong(vao: TruongNhiemVu): FormData {
  const f = new FormData();
  f.append('title', vao.title);
  f.append('description', vao.description);
  if (vao.deadline) f.append('deadline', vao.deadline);
  if (vao.leadOrgId) {
    f.append('lead_org_id', vao.leadOrgId);
    f.append('lead_org_name', vao.leadOrgName);
  }
  if (vao.file) f.append('file', vao.file);
  return f;
}

const banThat: typeof banGia = {
  thongKe: (f) => goiApi<ThongKeNhiemVu>(`${GOC}/tasks/dashboard`, { query: { ...f } }),
  cay: (f) => goiApi<TrangCayNhiemVu>(`${GOC}/tasks`, { query: { ...f } }),
  mot: (id) => goiApi<NutNhiemVu>(`${GOC}/tasks/${ma(id)}`),
  tao: (vao) => {
    const f = bieuMauTruong(vao);
    f.append('participants', JSON.stringify(vao.participants));
    if (vao.parentId) f.append('parent_id', vao.parentId);
    return goiApi<NutNhiemVu>(`${GOC}/tasks`, { method: 'POST', bieuMau: f });
  },
  sua: (id, vao) => {
    const f = bieuMauTruong(vao);
    if (!vao.leadOrgId) f.append('clear_lead_org', 'true');
    if (!vao.deadline) f.append('clear_deadline', 'true');
    return goiApi<NutNhiemVu>(`${GOC}/tasks/${ma(id)}`, { method: 'PATCH', bieuMau: f });
  },
  nguoiThamGia: (id, participants) =>
    goiApi<NutNhiemVu>(`${GOC}/tasks/${ma(id)}/participants`, { method: 'PUT', body: { participants } }),
  trangThai: (id, status) => goiApi<NutNhiemVu>(`${GOC}/tasks/${ma(id)}/status`, { method: 'PATCH', body: { status } }),
  xoa: (id) => goiApi<void>(`${GOC}/tasks/${ma(id)}`, { method: 'DELETE' }),
  tep: (id) => goiApiTep(`${GOC}/tasks/${ma(id)}/attachment`, '*/*'),
  dongThoiGian: (id) => goiApi<HoatDong[]>(`${GOC}/tasks/${ma(id)}/activities`),
  themCapNhat: (id, vao) => {
    const f = new FormData();
    f.append('kind', vao.kind);
    if (vao.content.trim()) f.append('content', vao.content.trim());
    if (vao.progress !== undefined) f.append('progress', String(vao.progress));
    if (vao.file) f.append('file', vao.file);
    return goiApi<HoatDong>(`${GOC}/tasks/${ma(id)}/activities`, { method: 'POST', bieuMau: f });
  },
  tepCapNhat: (taskId, activityId) =>
    goiApiTep(`${GOC}/tasks/${ma(taskId)}/activities/${ma(activityId)}/attachment`, '*/*'),
  goiYPhanRa: (id) => goiApi<GoiYPhanRa>(`${GOC}/tasks/${ma(id)}/decompose/suggest`, { method: 'POST' }),
  taoViecCon: (id, children) =>
    goiApi<NutNhiemVu>(`${GOC}/tasks/${ma(id)}/children`, { method: 'POST', body: { children } }),
  thongBao: (limit) => goiApi<BangTinThongBao>(`${GOC}/tasks/notifications`, { query: { limit } }),
  daXem: () => goiApi<void>(`${GOC}/tasks/notifications/read`, { method: 'POST' }),
  donViDanhBa: () =>
    goiApi<TrangDanhBa<DonViDanhBa>>(`${GOC}/directory/organizations`, { query: { page_size: DANH_BA_TOI_DA } }),
  nguoiDanhBa: (search, pageSize) =>
    goiApi<TrangDanhBa<NguoiDanhBa>>(`${GOC}/directory/users`, { query: { search, page_size: pageSize } }),
};

const chon = () => (dungApiThat() ? banThat : banGia);

export const nhiemVuRepository = {
  thongKe: (f: LocKhoangNgay) => chon().thongKe(f),
  cay: (f: LocCay) => chon().cay(f),
  mot: (id: string) => chon().mot(id),
  tao: (vao: GiaoNhiemVuVao) => chon().tao(vao),
  sua: (id: string, vao: TruongNhiemVu) => chon().sua(id, vao),
  nguoiThamGia: (id: string, ps: NguoiThamGiaVao[]) => chon().nguoiThamGia(id, ps),
  trangThai: (id: string, status: TrangThaiNhiemVu) => chon().trangThai(id, status),
  xoa: (id: string) => chon().xoa(id),
  tep: (id: string) => chon().tep(id),
  dongThoiGian: (id: string) => chon().dongThoiGian(id),
  themCapNhat: (id: string, vao: CapNhatVao) => chon().themCapNhat(id, vao),
  tepCapNhat: (taskId: string, activityId: string) => chon().tepCapNhat(taskId, activityId),
  goiYPhanRa: (id: string) => chon().goiYPhanRa(id),
  taoViecCon: (id: string, con: ViecConVao[]) => chon().taoViecCon(id, con),
  thongBao: (limit: number) => chon().thongBao(limit),
  daXem: () => chon().daXem(),
  donViDanhBa: () => chon().donViDanhBa(),
  nguoiDanhBa: (search: string | undefined, pageSize: number) => chon().nguoiDanhBa(search, pageSize),
};
